/** Gemeinsame Typen und Hilfen für den Portal-Dokumente-Hub. */

import { formatPortalUploadError } from './portalErrorMessages';

export interface PortalProtokoll {
  id: string;
  filename: string;
  nc_url: string;
  nextcloud_path?: string;
  created: string;
  updated?: string;
  last_revision_id?: number;
  last_save_callback_at?: string;
  /** Bytes aus PB/NC wenn verfügbar */
  file_size?: number;
  mime_type?: string;
}

export interface PortalRecentDocument {
  display_name: string;
  pb_record_id?: string;
  nextcloud_path?: string;
  document_url: string;
  openedAt: string;
}

/** Slice 1.3: Offene Editor-Tabs — Metadaten für ARKO-Webhook (ohne Volltext). */
export interface PortalOpenDocumentMeta {
  documentId: string;
  title: string;
  nc_path: string | null;
  /** true = aktuell fokussiertes Dokument */
  active?: boolean;
}

/** Slice 1.3b: Gewählte Docs für Synthese — voller Kontext/Extract pro Doc. */
export interface PortalSelectedDocumentPayload {
  documentId: string;
  title: string;
  nc_path: string | null;
  documentContext: string;
  documentExtractFingerprint?: string;
}

/** Max. gleichzeitig geöffnete OnlyOffice-Editor-Tabs (reine UI-Liste; nur ein Embed aktiv). */
export const PORTAL_MAX_OPEN_DOCUMENTS = 10;

/** Max. gleichzeitig aktive ARKO-Chat-Tabs (Workspace localStorage). */
export const PORTAL_MAX_OPEN_CHATS = 10;

/** Max. gleichzeitig selektierbare Docs (Token-Budget V1). */
export const PORTAL_SYNTHESIS_MAX_SELECTED_DOCS = 5;

export function formatSynthesisDocumentLabels(titles: string[]): string | null {
  const clean = titles.map((t) => t.trim()).filter(Boolean);
  if (clean.length < 2) return null;
  return clean.join(' + ');
}

export function buildMetadataOnlySynthesisContext(meta: Pick<PortalOpenDocumentMeta, 'title' | 'nc_path'>): string {
  const title = meta.title.trim() || 'Dokument';
  const source = meta.nc_path?.trim() || '(unbekannt)';
  return [
    `Dokument: ${title}`,
    `Quelle: ${source}`,
    '',
    'Hinweis: Volltext noch nicht geladen — bitte den Tab einmal aktivieren oder den Auszug in den Chat kopieren.',
  ].join('\n');
}

export const PORTAL_RECENT_DOCS_MAX = 15;

export const PORTAL_DOCUMENT_FILE_ACCEPT =
  '.pdf,.doc,.docx,.odt,.rtf,.txt,.md,.markdown,.html,.htm,.csv,.xls,.xlsx,.ods,.ppt,.pptx,.odp,audio/*,.m4a,.mp3,.wav,.ogg,.webm,.aac,.opus,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/markdown,text/plain,text/html,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation';

const AUDIO_EXT = new Set(['m4a', 'mp3', 'wav', 'ogg', 'webm', 'aac', 'opus', 'flac']);

export function fileExtensionFromName(name: string): string {
  const m = name.trim().toLowerCase().match(/\.([a-z0-9]+)$/);
  return m ? m[1] : '';
}

/** Anzeige für Explorer-Spalte Größe. */
export function formatPortalFileSize(bytes?: number | null): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function portalDocumentTypeLabel(
  filename: string,
  mimeType?: string | null,
): string {
  const mime = mimeType?.trim().toLowerCase();
  if (mime) {
    if (mime.includes('pdf')) return 'PDF';
    if (mime.includes('word') || mime.includes('document')) return 'Word';
    if (mime.includes('spreadsheet') || mime.includes('excel')) return 'Tabelle';
    if (mime.includes('presentation')) return 'Präsentation';
    if (mime.startsWith('audio/')) return 'Audio';
    if (mime.startsWith('text/')) return 'Text';
  }
  const ext = fileExtensionFromName(filename);
  if (!ext) return 'Datei';
  return ext.toUpperCase();
}

export function isAudioFile(file: File): boolean {
  if (file.type.startsWith('audio/')) return true;
  const ext = fileExtensionFromName(file.name);
  return AUDIO_EXT.has(ext);
}

export function isPortalPlainTextExtension(ext: string): boolean {
  return ext === 'txt' || ext === 'md' || ext === 'markdown' || ext === 'csv';
}

export function isPortalDocxForEditor(filename: string, nextcloudPath?: string): boolean {
  const ext =
    fileExtensionFromName(filename) || fileExtensionFromName(nextcloudPath || '');
  return ext === 'docx' || ext === 'doc';
}

export function canOpenDocumentInPortal(
  isPremium: boolean,
  filename: string,
  nextcloudPath: string | undefined,
  documentUrl: string | undefined,
): boolean {
  if (!isPremium || !documentUrl) return false;
  const ext =
    fileExtensionFromName(filename) ||
    fileExtensionFromName(nextcloudPath || '') ||
    fileExtensionFromName(documentUrl);
  return (
    isPortalDocxForEditor(filename, nextcloudPath) ||
    isPortalPlainTextExtension(ext) ||
    isPortalPdfDocument(filename, nextcloudPath, documentUrl)
  );
}

/** PB `portal_documents`: aktive Zeilen fuer Hub-Liste (ohne archivierte). */
export function isActivePortalDocumentRecord(record: Record<string, unknown>): boolean {
  const st = record.status;
  if (st === 'archived') return false;
  return st === 'active' || st === 'draft' || st == null || st === '';
}

/** PB `portal_documents`: Filter nach Org (kanzlei_id) oder Legacy-Sitz (customer_id). */
export function buildPortalDocumentsPbFilter(customerId: string, kanzleiId?: string): string {
  const kid = (kanzleiId || '').replace(/\\/g, '\\\\').replace(/'/g, "\\'");
  if (kid) {
    return `(kanzlei_id='${kid}')`;
  }
  const cid = customerId.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
  return `(customer_id='${cid}')`;
}

/** PB list sort — `-created`/`-updated` → HTTP 400 auf pb-labor/pb-thomas (vgl. memcore.ts, lessons 12.06.2026). */
export const PORTAL_DOCUMENTS_PB_LIST_SORT = '-id';

export function mapPbRecordToProtokoll(r: Record<string, unknown>): PortalProtokoll {
  const id = typeof r.id === 'string' ? r.id : String(r.id ?? '');
  const ncUrl =
    (typeof r.document_url === 'string' && r.document_url.trim()) ||
    (typeof r.nc_url === 'string' && r.nc_url.trim()) ||
    '';
  const filename =
    (typeof r.display_name === 'string' && r.display_name.trim()) ||
    (typeof r.filename === 'string' && r.filename.trim()) ||
    'Dokument';
  const created = typeof r.created === 'string' ? r.created : new Date().toISOString();
  const nextcloud_path =
    typeof r.nextcloud_path === 'string' && r.nextcloud_path.trim()
      ? r.nextcloud_path.trim()
      : undefined;
  const updated = typeof r.updated === 'string' ? r.updated : undefined;
  const revRaw = r.last_revision_id;
  const revNum = revRaw != null ? Number(revRaw) : NaN;
  const last_revision_id =
    Number.isFinite(revNum) && revNum >= 0 ? Math.floor(revNum) : undefined;
  const last_save_callback_at =
    typeof r.last_save_callback_at === 'string' && r.last_save_callback_at.trim()
      ? r.last_save_callback_at.trim()
      : undefined;
  const sizeRaw = r.file_size ?? r.size ?? r.filesize;
  const sizeNum = sizeRaw != null ? Number(sizeRaw) : NaN;
  const file_size = Number.isFinite(sizeNum) && sizeNum >= 0 ? Math.floor(sizeNum) : undefined;
  const mime_type =
    typeof r.mime_type === 'string' && r.mime_type.trim()
      ? r.mime_type.trim()
      : typeof r.content_type === 'string' && r.content_type.trim()
        ? r.content_type.trim()
        : undefined;
  return {
    id,
    filename,
    nc_url: ncUrl,
    nextcloud_path,
    created,
    updated,
    last_revision_id,
    last_save_callback_at,
    file_size,
    mime_type,
  };
}

/** Cache-Bust fuer OnlyOffice document.url (NC-Share-Links cachen sonst alte Bytes). */
export function bustPortalDocumentDownloadUrl(
  url: string,
  bustToken?: string | number,
): string {
  const raw = url.trim();
  if (!/^https?:\/\//i.test(raw)) return raw;
  if (bustToken == null || bustToken === '') return raw;
  const token = String(bustToken);
  try {
    const u = new URL(raw);
    u.searchParams.set('v', token);
    return u.toString();
  } catch {
    const sep = raw.includes('?') ? '&' : '?';
    return `${raw}${sep}v=${encodeURIComponent(token)}`;
  }
}

export function portalRecentDocsStorageKey(customerId: string): string {
  return `arkorbit-portal-recent-docs:${customerId}`;
}

export function portalVirtualFoldersStorageKey(customerId: string): string {
  return `arkorbit-portal-virtual-folders:${customerId}`;
}

export function loadPortalVirtualFolders(customerId: string): string[] {
  try {
    const raw = localStorage.getItem(portalVirtualFoldersStorageKey(customerId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x): x is string => typeof x === 'string' && Boolean(x.trim()))
      .map((x) => sanitizePortalFolderInput(x))
      .filter(Boolean);
  } catch {
    return [];
  }
}

export function savePortalVirtualFolders(customerId: string, folders: string[]): void {
  const unique = Array.from(
    new Set(folders.map((f) => sanitizePortalFolderInput(f)).filter(Boolean)),
  );
  try {
    localStorage.setItem(portalVirtualFoldersStorageKey(customerId), JSON.stringify(unique));
  } catch {
    /* localStorage voll oder blockiert */
  }
}

export function loadRecentPortalDocuments(customerId: string): PortalRecentDocument[] {
  try {
    const raw = localStorage.getItem(portalRecentDocsStorageKey(customerId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (x): x is PortalRecentDocument =>
          Boolean(x) &&
          typeof x === 'object' &&
          typeof (x as PortalRecentDocument).display_name === 'string' &&
          typeof (x as PortalRecentDocument).document_url === 'string' &&
          typeof (x as PortalRecentDocument).openedAt === 'string',
      )
      .slice(0, PORTAL_RECENT_DOCS_MAX);
  } catch {
    return [];
  }
}

export function saveRecentPortalDocument(customerId: string, entry: PortalRecentDocument): void {
  const existing = loadRecentPortalDocuments(customerId);
  const dedupeKey = entry.pb_record_id || entry.nextcloud_path || entry.document_url;
  const filtered = existing.filter((e) => {
    const k = e.pb_record_id || e.nextcloud_path || e.document_url;
    return k !== dedupeKey;
  });
  const next = [entry, ...filtered].slice(0, PORTAL_RECENT_DOCS_MAX);
  try {
    localStorage.setItem(portalRecentDocsStorageKey(customerId), JSON.stringify(next));
  } catch {
    /* localStorage voll oder blockiert */
  }
}

/** Entfernt Browser-only „Zuletzt geöffnet“, wenn PB-Liste leer ist (stale NC-Shares → Extract-502). */
export function clearRecentPortalDocumentsWhenServerEmpty(
  customerId: string,
  serverRows: Pick<PortalProtokoll, 'id'>[],
): boolean {
  if (serverRows.length > 0) return false;
  try {
    const key = portalRecentDocsStorageKey(customerId);
    if (!localStorage.getItem(key)) return false;
    localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

export type PortalDocumentPathGroup = 'portal-documents' | 'contracts-final' | 'other';

export const PORTAL_DOCUMENT_GROUP_ORDER: PortalDocumentPathGroup[] = [
  'portal-documents',
  'contracts-final',
  'other',
];

export const PORTAL_DOCUMENT_GROUP_META: Record<
  PortalDocumentPathGroup,
  { label: string; hint: string }
> = {
  'portal-documents': {
    label: 'Portal-Dokumente',
    hint: 'Uploads und bearbeitete Dateien',
  },
  'contracts-final': {
    label: 'Verträge & Abschlüsse',
    hint: 'Generierte Vertragsdokumente',
  },
  other: {
    label: 'Weitere Dokumente',
    hint: 'Protokolle und sonstige Ablage',
  },
};

export function classifyPortalDocumentGroup(
  nextcloudPath?: string,
  filename?: string,
): PortalDocumentPathGroup {
  const hay = `${nextcloudPath || ''} ${filename || ''}`.toLowerCase();
  if (hay.includes('portal-documents') || hay.includes('portal_documents')) {
    return 'portal-documents';
  }
  if (hay.includes('contracts-final') || hay.includes('contracts_final')) {
    return 'contracts-final';
  }
  return 'other';
}

/** Relativer Ordnerpfad unter `portal-documents/` (leer = Wurzel). */
export function portalDocumentRelativeFolder(nextcloudPath?: string): string {
  const raw = String(nextcloudPath || '').trim();
  if (!raw) return '';
  const norm = raw.replace(/\\/g, '/');
  const marker = '/portal-documents/';
  const idx = norm.toLowerCase().indexOf(marker);
  if (idx < 0) return '';
  const after = norm.slice(idx + marker.length);
  const parts = after.split('/').filter(Boolean);
  if (!parts.length) return '';
  parts.pop();
  return parts.join('/');
}

export function sanitizePortalFolderInput(raw: string): string {
  return raw
    .split(/[\\/]+/)
    .map((p) => p.trim().replace(/[^\w.\- ()äöüÄÖÜß]+/g, '_').replace(/^\.+/, ''))
    .filter((p) => p && p !== '.' && p !== '..')
    .join('/');
}

/** Relativer NC-Ordner für Ordner-Upload (webkitRelativePath + aktueller UI-Ordner). */
export function portalUploadFolderPathFromWebkit(
  webkitRelativePath: string,
  baseFolder: string,
): string {
  const parts = webkitRelativePath.split(/[/\\]/).filter(Boolean);
  if (parts.length > 1) parts.pop();
  const subPath = parts.join('/');
  const combined =
    baseFolder.trim() && subPath
      ? `${baseFolder.trim()}/${subPath}`
      : baseFolder.trim() || subPath;
  return sanitizePortalFolderInput(combined);
}

const FOLDER_UPLOAD_SKIP = new Set(['.ds_store', 'thumbs.db', 'desktop.ini']);

type PortalFileWithRel = File & { webkitRelativePath?: string };

function portalFileRelativePath(file: File): string {
  return (file as PortalFileWithRel).webkitRelativePath || '';
}

function tagPortalFileRelativePath(file: File, relPath: string): File {
  if (!relPath) return file;
  try {
    Object.defineProperty(file, 'webkitRelativePath', {
      value: relPath,
      configurable: true,
      enumerable: true,
    });
  } catch {
    /* ignore — Browser ohne webkitRelativePath */
  }
  return file;
}

async function readPortalDirectoryEntry(
  entry: FileSystemDirectoryEntry,
  pathPrefix: string,
): Promise<File[]> {
  const reader = entry.createReader();
  const collected: File[] = [];

  const readBatch = (): Promise<void> =>
    new Promise((resolve, reject) => {
      reader.readEntries(
        (entries) => {
          if (!entries.length) {
            resolve();
            return;
          }
          void (async () => {
            for (const child of entries) {
              if (child.isFile) {
                const file = await new Promise<File>((res, rej) => {
                  (child as FileSystemFileEntry).file(res, rej);
                });
                collected.push(tagPortalFileRelativePath(file, `${pathPrefix}${file.name}`));
              } else if (child.isDirectory) {
                const nested = await readPortalDirectoryEntry(
                  child as FileSystemDirectoryEntry,
                  `${pathPrefix}${child.name}/`,
                );
                collected.push(...nested);
              }
            }
            await readBatch();
            resolve();
          })().catch(reject);
        },
        reject,
      );
    });

  await readBatch();
  return collected;
}

/** Drag&Drop: Ordner (mehrere Dateien, webkitRelativePath oder Directory-Entry). */
export function isPortalFolderFileDrop(files: FileList | null | undefined): boolean {
  if (!files?.length) return false;
  if (files.length > 1) return true;
  return /[/\\]/.test(portalFileRelativePath(files[0]));
}

/** Dist-verify Marker — verify-portal-ui-dist.sh (portalDnD); nicht entfernen. */
export const PORTAL_DND_DIST_MARKER = 'portalDnD:v1';

/** DragOver-Hilfe: externe Datei/Ordner (Windows liefert types oft erst nach preventDefault). */
export function portalDragMayAcceptExternalFiles(
  dataTransfer: DataTransfer | null | undefined,
): boolean {
  if (!dataTransfer) return false;
  const types = Array.from(dataTransfer.types || []);
  if (types.some((t) => t === 'Files' || t === 'application/x-moz-file')) return true;
  const items = dataTransfer.items;
  if (items?.length) {
    for (let i = 0; i < items.length; i += 1) {
      if (items[i].kind === 'file') return true;
    }
  }
  return Boolean(dataTransfer.files?.length);
}

/** DataTransfer: Ordner-Drop auch bei einzelnem Directory-Entry (Windows/Chrome). */
export function isPortalFolderDataTransfer(dataTransfer: DataTransfer | null | undefined): boolean {
  if (!dataTransfer) return false;
  if (isPortalFolderFileDrop(dataTransfer.files)) return true;
  const items = dataTransfer.items;
  if (!items?.length) return false;
  for (let i = 0; i < items.length; i += 1) {
    const entry = items[i].webkitGetAsEntry?.();
    if (entry?.isDirectory) return true;
  }
  return false;
}

/**
 * Sammelt alle Dateien aus einem Drop (inkl. Ordner-Rekursion via webkitGetAsEntry).
 */
export async function collectPortalDropFiles(
  dataTransfer: DataTransfer | null | undefined,
): Promise<File[]> {
  if (!dataTransfer) return [];

  const fromItems: File[] = [];
  const items = dataTransfer.items;
  if (items?.length) {
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      const entry = item.webkitGetAsEntry?.();
      if (!entry) {
        const file = item.getAsFile();
        if (file) fromItems.push(file);
        continue;
      }
      if (entry.isFile) {
        const file = await new Promise<File>((resolve, reject) => {
          (entry as FileSystemFileEntry).file(resolve, reject);
        });
        fromItems.push(file);
      } else if (entry.isDirectory) {
        const nested = await readPortalDirectoryEntry(
          entry as FileSystemDirectoryEntry,
          `${entry.name}/`,
        );
        fromItems.push(...nested);
      }
    }
  }

  if (fromItems.length) return fromItems;

  const list = dataTransfer.files;
  if (!list?.length) return [];
  // Marker im Bundle halten (DnD-Smoke / verify-portal-ui-dist.sh)
  if (PORTAL_DND_DIST_MARKER.length < 1) return [];
  return Array.from(list);
}

/** Dateien aus Ordner-Upload filtern (keine System-/Hidden-Dateien). */
export function filterPortalFolderUploadFiles(files: FileList | File[]): File[] {
  const list = Array.from(files);
  return list.filter((file) => {
    const name = file.name.trim();
    if (!name || name.startsWith('.')) return false;
    if (FOLDER_UPLOAD_SKIP.has(name.toLowerCase())) return false;
    const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath || name;
    if (rel.split(/[/\\]/).some((seg) => seg.startsWith('.'))) return false;
    return true;
  });
}

export interface PortalDocumentFolderNode {
  name: string;
  path: string;
  children: PortalDocumentFolderNode[];
  documents: PortalProtokoll[];
}

/** Ordnerbaum aus PB-Zeilen (nur Pfade unter portal-documents). */
export function buildPortalDocumentFolderTree(
  docs: PortalProtokoll[],
  currentFolder = '',
): PortalDocumentFolderNode {
  const root: PortalDocumentFolderNode = { name: '', path: '', children: [], documents: [] };
  const folderMap = new Map<string, PortalDocumentFolderNode>();
  folderMap.set('', root);

  const ensureFolder = (relPath: string, segment: string): PortalDocumentFolderNode => {
    const existing = folderMap.get(relPath);
    if (existing) return existing;
    const parentPath = relPath.includes('/')
      ? relPath.slice(0, relPath.lastIndexOf('/'))
      : '';
    const parent = ensureFolder(parentPath, parentPath.split('/').pop() || '');
    const node: PortalDocumentFolderNode = { name: segment, path: relPath, children: [], documents: [] };
    folderMap.set(relPath, node);
    parent.children.push(node);
    parent.children.sort((a, b) => a.name.localeCompare(b.name, 'de'));
    return node;
  };

  for (const doc of docs) {
    const rel = portalDocumentRelativeFolder(doc.nextcloud_path);
    if (!rel) {
      root.documents.push(doc);
      continue;
    }
    const segments = rel.split('/').filter(Boolean);
    let acc = '';
    for (const seg of segments) {
      acc = acc ? `${acc}/${seg}` : seg;
      ensureFolder(acc, seg);
    }
    const leaf = folderMap.get(rel);
    if (leaf) leaf.documents.push(doc);
    else root.documents.push(doc);
  }

  if (currentFolder) {
    return folderMap.get(currentFolder) || root;
  }
  return root;
}

export function portalFolderDisplayPath(folderPath: string): string {
  if (!folderPath.trim()) return 'Alle Dokumente';
  return folderPath.split('/').join(' / ');
}

/** n8n-Ingest → Hub-Zeile (PB-Id oder temporäre Zeile bis Index nachzieht). */
export function protokollFromIngestFields(fields: PortalDocumentOpenFields): PortalProtokoll | null {
  const pbId = fields.pb_record_id?.trim();
  const docUrl = fields.document_url?.trim();
  if (!docUrl) return null;
  const pathKey = fields.nextcloud_path?.trim() || docUrl;
  return {
    id: pbId || `pending:${pathKey}`,
    filename: fields.display_name?.trim() || 'Dokument',
    nc_url: docUrl,
    nextcloud_path: fields.nextcloud_path?.trim() || undefined,
    created: new Date().toISOString(),
    updated: fields.updated,
  };
}

export function isPortalPdfDocument(filename: string, nextcloudPath?: string, documentUrl?: string): boolean {
  const ext =
    fileExtensionFromName(filename) ||
    fileExtensionFromName(nextcloudPath || '') ||
    fileExtensionFromName(documentUrl || '');
  return ext === 'pdf';
}

export function mergeProtokollIntoList(
  list: PortalProtokoll[],
  item: PortalProtokoll,
): PortalProtokoll[] {
  const dedupeKey = item.id || item.nextcloud_path || item.nc_url;
  const filtered = list.filter((p) => {
    const k = p.id || p.nextcloud_path || p.nc_url;
    if (k === dedupeKey) return false;
    if (item.nextcloud_path && p.nextcloud_path === item.nextcloud_path) return false;
    if (item.nc_url && p.nc_url === item.nc_url) return false;
    return true;
  });
  return [item, ...filtered];
}

export function resolveProtokollDocumentUrl(
  protokolle: PortalProtokoll[],
  fields: { pb_record_id?: string; document_url: string },
): string {
  if (fields.pb_record_id) {
    const match = protokolle.find((p) => p.id === fields.pb_record_id);
    if (match?.nc_url) return match.nc_url;
  }
  return fields.document_url;
}

/** Frische Download-URL aus PB (z. B. wenn Hub-Liste noch leer oder Share-Link veraltet). */
export async function fetchPortalProtokollById(
  pbBaseUrl: string,
  collection: string,
  recordId: string,
  authToken: string,
): Promise<PortalProtokoll | null> {
  const base = pbBaseUrl.replace(/\/$/, '');
  const id = recordId.trim();
  const token = authToken.trim();
  if (!base || !id || !token) return null;
  try {
    const res = await fetch(`${base}/api/collections/${collection}/records/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const rec = (await res.json()) as Record<string, unknown>;
    if (!isActivePortalDocumentRecord(rec)) return null;
    const row = mapPbRecordToProtokoll(rec);
    return row.id ? row : null;
  } catch {
    return null;
  }
}

export function formatDeAtDateTime(iso: string): string {
  return new Date(iso).toLocaleDateString('de-AT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export interface PortalDocumentOpenFields {
  display_name: string;
  document_url: string;
  pb_record_id?: string;
  nextcloud_path?: string;
  updated?: string;
  last_revision_id?: number;
  last_save_callback_at?: string;
  /** n8n: PB-Index fehlgeschlagen, NC-Upload aber ok */
  pb_warning?: string;
}

/** n8n PORTAL_DOCUMENT_UPLOAD / NC-Import → Felder für Editor-Öffnung. */
export function parsePortalDocumentN8nResponse(
  data: Record<string, unknown>,
  res: Response,
): PortalDocumentOpenFields {
  const docUrl = typeof data.document_url === 'string' ? data.document_url.trim() : '';
  const payloadOk = data.ok === true || data.ok === 'true';
  if (!res.ok || (!payloadOk && !docUrl)) {
    throw new Error(formatPortalUploadError(res, data, 'upload'));
  }
  if (!docUrl) {
    throw new Error('Die Datei wurde gespeichert, aber die Server-Antwort fehlt. Bitte erneut versuchen.');
  }
  const dn = typeof data.display_name === 'string' ? data.display_name : 'Dokument';
  const pbUploadId =
    (typeof data.pb_record_id === 'string' && data.pb_record_id.trim()) ||
    (typeof data.pb_id === 'string' && data.pb_id.trim()) ||
    (typeof data.record_id === 'string' && data.record_id.trim()) ||
    undefined;
  const nextcloud_path =
    typeof data.nextcloud_path === 'string' && data.nextcloud_path.trim()
      ? data.nextcloud_path.trim()
      : undefined;
  const pbWarning =
    typeof data.pb_warning === 'string' && data.pb_warning.trim() ? data.pb_warning.trim() : undefined;
  return {
    display_name: dn,
    document_url: docUrl,
    pb_record_id: pbUploadId,
    nextcloud_path,
    pb_warning: pbWarning,
  };
}
